import React, { useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FieldOfAction } from 'api-green-node';
import { ButtonGroup } from '@paygreen/paygreen-ui';
import { useAPIError } from '../../../../context';
import {
    CancelButton,
    ValidateButton,
    ModalForm,
} from '../../../../components';
import { putAssociation } from '../../../user/service';
import CharityEditForm from '../../../user/charity/CharityEditForm';

const CharityManagementModal = ({
    t,
    setOpen,
    isOpen,
    accountData,
    setAccountData,
    setIsLoading,
    refresh,
}) => {
    const { addError } = useAPIError();

    // initializing all react-hook-form values and methods
    const defaultValues = {
        name: accountData?.association?.name,
        description: accountData?.association?.description,
        url: accountData?.association?.url,
        fieldOfAction: t('charity.edit.fieldOfActionOptions')
            .indexOf(
                t(
                    `charity.edit.fieldOfActionValues.${accountData?.association?.fieldOfAction}`,
                ).toUpperCase(),
            )
            .toString(),
    };

    const methods = useForm({ defaultValues });

    const { register, handleSubmit, reset, clearErrors } = methods;

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'name' });
        register({ name: 'description' });
        register({ name: 'url' });
        register({ name: 'fieldOfAction' });
    }, [register]);

    // Update selector default values in form when fieldOfAction data is retrieved to guarantee translation and form mounted
    useEffect(() => {
        reset({
            name: accountData?.association?.name,
            description: accountData?.association?.description,
            url: accountData?.association?.url,
            fieldOfAction: t('charity.edit.fieldOfActionOptions')
                .indexOf(
                    t(
                        `charity.edit.fieldOfActionValues.${accountData?.association?.fieldOfAction}`,
                    ).toUpperCase(),
                )
                .toString(),
        });
    }, [accountData, reset, t]);

    /**
     * PATCH request to API to update user's data
     * @returns {object} - object with user's data updated
     */
    const updateCharity = useCallback(
        (id, data) => {
            setIsLoading(true);

            putAssociation(id, data)
                .then((res) => {
                    const newCharity = res.data;
                    setAccountData({
                        ...accountData,
                        association: newCharity,
                    });
                    refresh();
                    toast(t('form.toastSuccess.charityAdminUpdate'));
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    addError(detail, status);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [addError, setIsLoading, accountData, t, setAccountData, refresh],
    );

    const submitForm = (data) => {
        setOpen(false);

        const { name, description, url, fieldOfAction } = data;

        const updatedCharity = {
            name,
            description,
            url,
            fieldOfAction: FieldOfAction[fieldOfAction],
            visible: accountData?.association?.visible,
        };

        updateCharity(accountData?.association?.idAssociation, updatedCharity);
    };

    const cancelCloseModal = () => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            clearErrors();
            reset(defaultValues);
        }, 500);
    };

    return (
        <ModalForm
            modalTitle={t('charity.charityAdminCard')}
            blockWidth="md"
            paddingLateral="md"
            isOpen={isOpen}
            actionOnClose={cancelCloseModal}
        >
            <FormProvider {...methods}>
                <CharityEditForm
                    onSubmit={handleSubmit(submitForm)}
                    defaultValues={defaultValues}
                >
                    <ButtonGroup marginTop="sm" marginBottom="sm">
                        <CancelButton
                            onClick={cancelCloseModal}
                            buttonSize="md"
                        >
                            {t('form.button.cancel')}
                        </CancelButton>

                        <ValidateButton buttonSize="md">
                            {t('form.button.save')}
                        </ValidateButton>
                    </ButtonGroup>
                </CharityEditForm>
            </FormProvider>
        </ModalForm>
    );
};

CharityManagementModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    accountData: PropTypes.object,
    setIsLoading: PropTypes.func,
    setAccountData: PropTypes.func,
    isShop: PropTypes.bool,
    refresh: PropTypes.func,
};

CharityManagementModal.defaultProps = {
    isShop: true,
};

export default withTranslation('common')(CharityManagementModal);
